import React, { useEffect, useState } from 'react';
import { ArrowRight, Check, Sparkles, UserCheck } from 'lucide-react';
import useInView from './useInView';

/**
 * One encounter, every funder — existing and new.
 *
 * A CHW visit fans out into four ledger lines for the funders the
 * organization already bills. Each moves from Drafted (an agent wrote it) to
 * Approved (a person signed it) to the state that funder's channel ends in.
 * Then the opportunity finder (Funding & Growth, TRUS-776) adds two funders
 * the same visit already qualifies for: Found, then Outreach approved, then
 * Added — and the visit is attributed to them as well. That second beat is
 * the point: one transaction, attributed to existing and new funders, never
 * double-billed for the same service line.
 *
 * The services and funders are the seed deck's (slide 4); a grant line ends
 * at Attributed rather than Paid because drawdown is not built yet. Reduced
 * motion shows the end state.
 */

type Stage = 0 | 1 | 2 | 3;

interface Line {
  service: string;
  funder: string;
  program: string;
  channel: string;
  /** Chip text for stages 1, 2 and 3. */
  labels: [string, string, string];
  isNew?: boolean;
}

const EXISTING: Line[] = [
  { service: 'CHW home visit', funder: 'Medicaid plan', program: 'CHW benefit', channel: '837P via clearinghouse', labels: ['Drafted', 'Approved', 'Billed'] },
  { service: 'Food & housing screen', funder: 'Medicaid plan', program: 'Community Supports', channel: 'Plan invoice workbook', labels: ['Drafted', 'Approved', 'Billed'] },
  { service: 'Housing navigation', funder: 'County contract', program: 'Homeless response', channel: 'County portal', labels: ['Drafted', 'Approved', 'Submitted'] },
  { service: 'Doula referral', funder: 'Foundation grant', program: 'Perinatal program', channel: 'Grant report', labels: ['Drafted', 'Approved', 'Attributed'] },
];

const NEW_FUNDERS: Line[] = [
  { service: 'Prenatal care coordination', funder: 'County public health', program: 'Perinatal home visiting grant', channel: 'Found by the opportunity finder', labels: ['Found', 'Outreach approved', 'Added'], isNew: true },
  { service: 'Transport to appointment', funder: 'Medicare Advantage plan', program: 'Supplemental benefit', channel: 'Found by the opportunity finder', labels: ['Found', 'Outreach approved', 'Added'], isNew: true },
];

const LINES = [...EXISTING, ...NEW_FUNDERS];

const chip = (stage: Stage, line: Line) => {
  if (stage === 0) return null;
  const text = line.labels[stage - 1];
  const done = stage === 3;
  const style = done
    ? line.isNew
      ? 'bg-v2accent-soft text-v2accent border-v2accent'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30'
    : stage === 2
      ? 'bg-v2accent-soft text-ink border-v2border'
      : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border transition-colors ${style}`}>
      {done && <Check className="w-3 h-3" aria-hidden="true" />}
      {text}
    </span>
  );
};

/** When each line's stages fire: existing funders first, new funders after a pause. */
const delayFor = (i: number, s: Stage) => {
  const isNew = i >= EXISTING.length;
  const base = isNew ? 400 + EXISTING.length * 450 + 2 * 1100 + 1400 + (i - EXISTING.length) * 600 : 400 + i * 450;
  return base + (s - 1) * 1100;
};

const Row: React.FC<{ line: Line; stage: Stage }> = ({ line, stage }) => (
  <li
    className={`grid grid-cols-1 sm:grid-cols-[1.1fr_1fr_auto] gap-2 sm:gap-4 items-center rounded-xl border px-4 py-3 transition-all duration-500 ${
 stage > 0
 ? `opacity-100 translate-x-0 ${
            line.isNew
              ? 'border-dashed border-v2accent bg-v2accent-soft'
              : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
          }`
 : 'opacity-0 -translate-x-3 border-transparent'
 }`}
  >
    <div>
      <p className="font-semibold text-gray-900 dark:text-gray-100">{line.service}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{line.channel}</p>
    </div>
    <div>
      <p className="font-semibold text-v2accent">{line.funder}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{line.program}</p>
    </div>
    <div className="sm:text-right min-w-[132px]">{chip(stage, line)}</div>
  </li>
);

const OneEncounter: React.FC = () => {
  const { ref, inView, reduced } = useInView<HTMLDivElement>();
  const [stages, setStages] = useState<Stage[]>(LINES.map(() => 0));

  useEffect(() => {
    if (!inView) return;
    if (reduced) {
      setStages(LINES.map(() => 3));
      return;
    }
    const timers: number[] = [];
    LINES.forEach((_, i) => {
      ([1, 2, 3] as Stage[]).forEach((s) => {
        timers.push(
          window.setTimeout(() => {
            setStages((prev) => prev.map((v, j) => (j === i ? s : v)) as Stage[]);
          }, delayFor(i, s)),
        );
      });
    });
    return () => timers.forEach(clearTimeout);
  }, [inView, reduced]);

  const newShown = stages.slice(EXISTING.length).some((s) => s > 0);

  return (
    <section className="py-16 bg-white dark:bg-gray-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-2 text-center">One encounter, every funder</p>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 text-center">
          Every service attributed to the funder that pays for it.
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-center max-w-2xl mx-auto mb-10">
          First the funders you already bill. Then the ones the same visit qualifies you for, found and reached with you.
        </p>
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-[minmax(0,280px)_40px_1fr] gap-6 items-start">
          <div className="rounded-2xl bg-v2accent-soft border border-v2border text-ink p-6 lg:sticky lg:top-24">
            <p className="text-xs font-semibold tracking-widest text-v2accent uppercase mb-2">One encounter</p>
            <p className="text-2xl font-bold leading-tight mb-2">A CHW visits a pregnant member</p>
            <p className="text-sm text-ink-500">Captured once, on one record, in the member’s language.</p>
          </div>
          <ArrowRight className="hidden lg:block w-8 h-8 text-gray-300 mx-auto mt-10" aria-hidden="true" />
          <div aria-live="polite">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Funders you already bill</p>
            <ul className="space-y-3">
              {EXISTING.map((l, i) => (
                <Row key={l.service} line={l} stage={stages[i]} />
              ))}
            </ul>
            <p
              className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mt-6 mb-2 transition-opacity duration-500 ${
 newShown ? 'opacity-100 text-v2accent' : 'opacity-0'
 }`}
            >
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              New funders the same visit qualifies for
            </p>
            <ul className="space-y-3">
              {NEW_FUNDERS.map((l, k) => (
                <Row key={l.service} line={l} stage={stages[EXISTING.length + k]} />
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300 text-center">
          <UserCheck className="w-4 h-4 text-v2accent" aria-hidden="true" />
          Agents draft each line and each outreach. A named person approves them, and no service line is billed to two
          funders.
        </p>
      </div>
    </section>
  );
};

export default OneEncounter;
